import { EntityBase } from "./EntityBase";

/**
 * Parameter Type enum
 */
export enum ParameterType {
    STRING = "string",
    NUMBER = "number",
    BOOLEAN = "boolean",
    ARRAY = "array",
    OBJECT = "object",
    DATE = "date",
    TIME = "time",
    DATETIME = "datetime",
    TIMESTAMP = "timestamp",
    FILE = "file",
    ANY = "any",
    UNKNOWN = "unknown",
}

/**
 * Function Attachment Type
 * Defines where this function can be attached
 */
export enum FunctionAttachmentType {
    SERVICE = "service",
    MIDDLEWARE = "middleware",
    ENDPOINT = "endpoint",
}

/**
 * Parameters Interface
 * Represents a function parameter
 */
export interface ParametersInterface {
    /** Name of the parameter */
    parameterName: string;

    /** Type of the parameter */
    parameterType: ParameterType;

    /** Whether the parameter is required */
    required: boolean;

    /** Schema definition for complex types (objects, arrays) */
    schema?: Record<string, ParameterType | string>;

    /** Default value for the parameter */
    defaultValue?: unknown;

    /** Description of the parameter */
    description?: string;

    /** Validation rules */
    validation?: {
        min?: number;
        max?: number;
        minLength?: number;
        maxLength?: number;
        pattern?: string;
        enum?: unknown[];
    };
}

/**
 * Function Interface
 * Represents a function that can be attached to Services, Middlewares, or Endpoints
 */
export interface FunctionInterface extends EntityBase {
    /** Name of the function */
    functionName: string;

    /** Description of what this function does (required for functions) */
    description: string;

    /** Input parameters for the function */
    inputParameters: ParametersInterface[];

    /** Output/return type of the function */
    output: ParametersInterface;

    /** Implementation steps/instructions for the function */
    implementationSteps: string[];

    /** Actual implementation code (if provided) */
    implementation?: string;

    /** Where this function can be attached */
    attachmentTypes: FunctionAttachmentType[];

    /** SDKs/libraries used by this function */
    sdkUsed: string[];

    /** Services needed by this function */
    servicesNeeded: string[];

    /** External functions this function depends on */
    externalFunctionsNeeded: string[];

    /** Whether this is an async function */
    isAsync: boolean;

    /** Return type (TypeScript/Python type) */
    returnType?: string;

    /** Error handling strategy */
    errorHandling?: {
        throws?: boolean;
        returnsError?: boolean;
        errorTypes?: string[];
    };

    /** Dependencies this function requires */
    dependencies?: {
        packages?: string[];
        services?: string[];
        models?: string[];
    };

    /** Database models required by this function (model IDs or names) */
    modelsRequired?: string[];

}